import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ClientReservationService } from '../../../_services/api/reservation-client.service';
import { 
  ReservationCreate,
  ReservationDetail,
  ReservationEventType,
  Address
} from '../../../_models/reservation.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';

@Component({
  selector: 'app-member-reservation-create',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './member-reservation-create.html',
  styleUrl: './member-reservation-create.scss',
})
export class MemberReservationCreate implements OnInit, OnDestroy {
    reservationForm!: FormGroup;
    isLoading = false;
    isSubmitting = false;
    errorMessage = '';
    successMessage = '';
    
    eventTypes = Object.values(ReservationEventType);
    
    minStartDate = new Date();
    minEndDate = new Date();
    
    frenchTranslations = {
        required: 'Ce champ est requis',
        eventNameRequired: 'Le nom de l\'événement est requis',
        eventNameMaxLength: 'Le nom de l\'événement doit comporter moins de 255 caractères',
        eventTypeRequired: 'Le type d\'événement est requis',
        agentsRequired: 'Le nombre d\'agents est requis',
        agentsMin: 'Au moins 1 agent est requis',
        agentsMax: 'Maximum 50 agents autorisés',
        startDateRequired: 'La date de début est requise',
        startDateFuture: 'La date de début doit être future',
        endDateRequired: 'La date de fin est requise',
        endDateFuture: 'La date de fin doit être future',
        endDateAfterStart: 'La date de fin doit être après la date de début',
        streetNumberMaxLength: 'Le numéro de rue doit comporter moins de 20 caractères',
        streetRequired: 'La rue est requise pour ce type d\'événement',
        streetMaxLength: 'La rue doit comporter moins de 255 caractères',
        postalCodeRequired: 'Le code postal est requis',
        postalCodeMaxLength: 'Le code postal doit comporter moins de 20 caractères',
        postalCodePattern: 'Veuillez saisir un code postal valide (lettres, chiffres, espaces et tirets uniquement)',
        cityRequired: 'La ville est requise',
        cityMaxLength: 'La ville doit comporter moins de 100 caractères',
        descriptionMaxLength: 'La description doit comporter moins de 1000 caractères',
        creatingReservation: 'Création de réservation en cours...',
        confirmCreate: 'Créer la réservation',
        reservationCreateSuccess: 'Réservation créée avec succès !',
        reservationCreateFailed: 'Échec de la création de la réservation. Veuillez réessayer.',
        eventTypes: {
        SECURITY_GUARDING: 'Garde de Sécurité',
        EVENT_SECURITY: 'Sécurité Événementielle',
        PERSONAL_PROTECTION: 'Protection Personnelle',
        SURVEILLANCE: 'Surveillance',
        SECURITY_AUDIT: 'Audit de Sécurité',
        TRANSPORT_SECURITY: 'Sécurité de Transport',
        OTHER: 'Autre'
        }
    };
    
    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private reservationService: ClientReservationService,
        private notificationService: NotificationService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        this.initializeForm();
    }

    ngOnInit(): void {
    const now = new Date();
    const startDateTime = this.formatDateTimeForInput(new Date(now.getTime() + 3600000)); // 1 hour from now
    const endDateTime = this.formatDateTimeForInput(new Date(now.getTime() + 7200000)); // 2 hours from now
    
    this.reservationForm.patchValue({
        startDateTime: startDateTime,
        endDateTime: endDateTime
    });
    
    this.reservationForm.get('startDateTime')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
        const startDateValue = this.reservationForm.get('startDateTime')?.value;
        const endDateValue = this.reservationForm.get('endDateTime')?.value;
        
        if (startDateValue && endDateValue) {
            const startDate = new Date(startDateValue);
            const endDate = new Date(endDateValue);
            
            if (endDate <= startDate) {
            const newEndDate = new Date(startDate.getTime() + 3600000);
            this.reservationForm.get('endDateTime')?.setValue(this.formatDateTimeForInput(newEndDate));
            }
        }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeForm(): void {
        this.reservationForm = this.fb.group({
        eventName: ['', [
            Validators.required,
            Validators.maxLength(255)
        ]],
        reservationEventType: ['', Validators.required],
        requiredAgents: [1, [
            Validators.required,
            Validators.min(1),
            Validators.max(50)
        ]],
        startDateTime: ['', [
            Validators.required,
            this.futureDateValidator()
        ]],
        endDateTime: ['', [
            Validators.required,
            this.futureDateValidator()
        ]],
        description: ['', Validators.maxLength(1000)],
        address: this.fb.group({
            street: ['', Validators.maxLength(255)],
            streetNumber: ['', Validators.maxLength(20)],
            postalCode: ['', [
            Validators.maxLength(20),
            Validators.pattern(/^[0-9A-Z\s\-]*$/)
            ]],
            city: ['', Validators.maxLength(100)]
        })
        });

        this.setupFormListeners();
    }

    private setupFormListeners(): void {
        this.reservationForm.get('startDateTime')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
            this.updateDateValidators();
        });

        this.reservationForm.get('reservationEventType')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
            this.updateAddressValidators();
        });
    }

    private updateDateValidators(): void {
        const startDate = this.reservationForm.get('startDateTime')?.value;
        if (startDate) {
        const startDateTime = new Date(startDate);
        this.minEndDate = new Date(startDateTime.getTime() + 3600000);
        
        const endDateControl = this.reservationForm.get('endDateTime');
        if (endDateControl?.value) {
            const endDateTime = new Date(endDateControl.value);
            if (endDateTime <= startDateTime) {
            endDateControl.setErrors({ ...endDateControl.errors, dateSequence: true });
            }
        }
        }
    }

    private updateAddressValidators(): void {
        const eventType = this.reservationForm.get('reservationEventType')?.value;
        const addressGroup = this.reservationForm.get('address') as FormGroup;
        
        const requiresAddress = [
        ReservationEventType.SECURITY_GUARDING,
        ReservationEventType.EVENT_SECURITY,
        ReservationEventType.PERSONAL_PROTECTION
        ].includes(eventType);

        Object.keys(addressGroup.controls).forEach(key => {
        const control = addressGroup.get(key);
        if (requiresAddress && key !== 'streetNumber') {
            control?.setValidators([Validators.required, ...(control.validator ? [control.validator] : [])]);
        } else {
            const currentValidators = control?.validator;
            if (currentValidators) {
            const newValidators = [];
            if (currentValidators.toString().includes('maxLength')) newValidators.push(Validators.maxLength(control?.errors?.['maxlength']?.requiredLength));
            if (currentValidators.toString().includes('pattern')) newValidators.push(Validators.pattern(control?.errors?.['pattern']?.requiredPattern));
            control?.setValidators(newValidators.length > 0 ? newValidators : null);
            } else {
            control?.clearValidators();
            }
        }
        control?.updateValueAndValidity();
        });
    }

    getMinStartDate(): string {
        const now = new Date();
        // allow current date but ensure time is not in the past
        now.setMinutes(now.getMinutes() - 5); // allow 5 minutes buffer
        return now.toISOString().slice(0, 16);
    }

    getMinEndDate(): string {
        const startDateValue = this.reservationForm.get('startDateTime')?.value;
        if (startDateValue) {
            const startDate = new Date(startDateValue);
            // minimum end date is 1 hour after start date
            const minEndDate = new Date(startDate.getTime() + 3600000);
            return minEndDate.toISOString().slice(0, 16);
        }
        return this.getMinStartDate();
    }

    private futureDateValidator() {
        return (control: any) => {
            if (!control.value) return null;        
            const selectedDate = new Date(control.value);
            const now = new Date();            
            return selectedDate >= now ? null : { pastDate: true };
        };
    }

    private formatDateTimeForInput(date: Date): string {
        return date.toISOString().slice(0, 16);
    }

    onSubmit(): void {
        if (this.reservationForm.invalid || this.isSubmitting) {
        this.markFormGroupTouched(this.reservationForm);
        return;
        }

        this.isSubmitting = true;
        this.errorMessage = '';
        this.successMessage = '';

        const formValue = this.reservationForm.value;
        
        const createData: ReservationCreate = {
        eventName: formValue.eventName,
        reservationEventType: formValue.reservationEventType,
        requiredAgents: formValue.requiredAgents,
        startDateTime: formValue.startDateTime,
        endDateTime: formValue.endDateTime,
        description: formValue.description || undefined,
        address: this.prepareAddress(formValue.address)
        };

        this.reservationService.createReservation(createData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
            next: (response: ApiResponse<ReservationDetail>) => {
            this.isSubmitting = false;
            this.successMessage = this.frenchTranslations.reservationCreateSuccess;
            setTimeout(() => {
                this.notificationService.showSuccess('Réservation créée avec succès');
                if (response.data?.id) {
                this.router.navigate(['/member/reservations/details', response.data.id]);
                } else {
                this.router.navigate(['/member/reservations']);
                }
            }, 500);
            },
            error: (error) => {
            this.isSubmitting = false;
            this.errorMessage = error.error?.message || this.frenchTranslations.reservationCreateFailed;
            if (isPlatformBrowser(this.platformId)) {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
            console.error('Reservation creation error:', error);
            }
        });
    }

    private formatDateTimeForApi(dateTimeString: string): string {
        const date = new Date(dateTimeString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    }

    private prepareAddress(addressFormValue: any): Address {
        return {
        street: addressFormValue.street?.trim() || undefined,
        streetNumber: addressFormValue.streetNumber?.trim() || undefined,
        postalCode: addressFormValue.postalCode?.trim() || undefined,
        city: addressFormValue.city?.trim() || undefined
        };
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach(control => {
        control.markAsTouched();
        if (control instanceof FormGroup) {
            this.markFormGroupTouched(control);
        }
        });
    }

    getEventTypeLabel(eventType: ReservationEventType): string {
        return this.frenchTranslations.eventTypes[eventType] || eventType;
    }

    get f() {
        if (!this.reservationForm || !this.reservationForm.controls) {
        return {};
        }
        return this.reservationForm.controls;
    }

    get addressForm(): FormGroup | null {
        const address = this.reservationForm?.get('address');
        return address instanceof FormGroup ? address : null;
    }

    hasError(controlName: string, errorName: string): boolean {
        const control = this.reservationForm?.get(controlName);
        return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
    }

    hasAddressError(controlName: string, errorName: string): boolean {
        const addressForm = this.addressForm;
        const control = addressForm?.get(controlName);
        return control ? control.hasError(errorName) && (control.touched || control.dirty) : false;
    }

    incrementAgents(): void {
        const currentValue = this.reservationForm.get('requiredAgents')?.value || 1;
        if (currentValue < 50) {
        this.reservationForm.get('requiredAgents')?.setValue(currentValue + 1);
        }
    }

    decrementAgents(): void {
        const currentValue = this.reservationForm.get('requiredAgents')?.value || 1;
        if (currentValue > 1) {
        this.reservationForm.get('requiredAgents')?.setValue(currentValue - 1);
        }
    }

    getErrorMessage(controlName: string, errorName: string): string {
        const errorMessages: { [key: string]: string } = {
        'eventName.required': this.frenchTranslations.eventNameRequired,
        'eventName.maxlength': this.frenchTranslations.eventNameMaxLength,
        'reservationEventType.required': this.frenchTranslations.eventTypeRequired,
        'requiredAgents.required': this.frenchTranslations.agentsRequired,
        'requiredAgents.min': this.frenchTranslations.agentsMin,
        'requiredAgents.max': this.frenchTranslations.agentsMax,
        'startDateTime.required': this.frenchTranslations.startDateRequired,
        'startDateTime.pastDate': this.frenchTranslations.startDateFuture,
        'endDateTime.required': this.frenchTranslations.endDateRequired,
        'endDateTime.pastDate': this.frenchTranslations.endDateFuture,
        'endDateTime.dateSequence': this.frenchTranslations.endDateAfterStart,
        'street.maxlength': this.frenchTranslations.streetMaxLength,
        'streetNumber.maxlength': this.frenchTranslations.streetNumberMaxLength,
        'postalCode.maxlength': this.frenchTranslations.postalCodeMaxLength,
        'postalCode.pattern': this.frenchTranslations.postalCodePattern,
        'city.maxlength': this.frenchTranslations.cityMaxLength,
        'description.maxlength': this.frenchTranslations.descriptionMaxLength,
        'street.required': this.frenchTranslations.streetRequired,
        'postalCode.required': this.frenchTranslations.postalCodeRequired,
        'city.required': this.frenchTranslations.cityRequired,
        };
        
        return errorMessages[`${controlName}.${errorName}`] || 'Erreur de validation';
    }

    goToList(): void {
        this.router.navigate(['/member/reservations']);
    }

    cancel(): void {
        this.goToList();
    }
}